import { Fragment, useMemo, type ReactNode } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { buildShotRefMap, type AgentShotRef } from "@/lib/shot-label"
import { cn } from "@/lib/utils"

const SHOT_TOKEN_RE = /@shot\[(\d+)\]/g
const LABEL_CANDIDATE_RE = /\b(\d+)\.(\d+)\b/g

type AgentMarkdownProps = {
  content: string
  className?: string
  shotRefs?: AgentShotRef[]
  onOpenShot?: (shotId: number) => void
}

function ShotLink({
  label,
  shotId,
  onOpenShot,
}: {
  label: string
  shotId: number
  onOpenShot?: (shotId: number) => void
}) {
  if (!onOpenShot) {
    return <span className="font-medium text-ui-accent">{label}</span>
  }

  return (
    <button
      type="button"
      className="rounded px-0.5 font-medium text-ui-accent underline decoration-ui-accent/40 underline-offset-2 hover:decoration-ui-accent"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onOpenShot(shotId)
      }}
    >
      {label}
    </button>
  )
}

function linkifyShotMentions(
  text: string,
  byId: Map<number, string>,
  byLabel: Map<string, number>,
  onOpenShot?: (shotId: number) => void,
): ReactNode[] {
  type Piece = { start: number; end: number; shotId: number; label: string }
  const pieces: Piece[] = []

  for (const match of text.matchAll(SHOT_TOKEN_RE)) {
    const id = Number(match[1])
    if (!Number.isFinite(id) || match.index == null) continue
    pieces.push({
      start: match.index,
      end: match.index + match[0].length,
      shotId: id,
      label: byId.get(id) ?? String(id),
    })
  }

  for (const match of text.matchAll(LABEL_CANDIDATE_RE)) {
    if (match.index == null) continue
    const label = match[0]
    const shotId = byLabel.get(label)
    if (shotId == null) continue
    // Skip if overlapping an existing @shot[id] span
    const overlaps = pieces.some((p) => match.index! < p.end && match.index! + label.length > p.start)
    if (overlaps) continue
    pieces.push({
      start: match.index,
      end: match.index + label.length,
      shotId,
      label,
    })
  }

  pieces.sort((a, b) => a.start - b.start || b.end - a.end)

  const merged: Piece[] = []
  for (const piece of pieces) {
    const last = merged[merged.length - 1]
    if (last && piece.start < last.end) continue
    merged.push(piece)
  }

  if (merged.length === 0) return [text]

  const nodes: ReactNode[] = []
  let cursor = 0
  merged.forEach((piece, idx) => {
    if (piece.start > cursor) nodes.push(text.slice(cursor, piece.start))
    nodes.push(
      <ShotLink
        key={`${piece.shotId}-${piece.start}-${idx}`}
        label={piece.label}
        shotId={piece.shotId}
        onOpenShot={onOpenShot}
      />,
    )
    cursor = piece.end
  })
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

function enrichChildren(
  children: ReactNode,
  byId: Map<number, string>,
  byLabel: Map<string, number>,
  onOpenShot?: (shotId: number) => void,
): ReactNode {
  if (typeof children === "string") {
    return <>{linkifyShotMentions(children, byId, byLabel, onOpenShot)}</>
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => <Fragment key={i}>{enrichChildren(child, byId, byLabel, onOpenShot)}</Fragment>)
  }
  return children
}

export function AgentMarkdown({ content, className, shotRefs = [], onOpenShot }: AgentMarkdownProps) {
  const { byId, byLabel } = useMemo(() => buildShotRefMap(shotRefs), [shotRefs])

  const components = useMemo(
    () => ({
      p: ({ children }: { children?: ReactNode }) => <p>{enrichChildren(children, byId, byLabel, onOpenShot)}</p>,
      li: ({ children }: { children?: ReactNode }) => <li>{enrichChildren(children, byId, byLabel, onOpenShot)}</li>,
      td: ({ children }: { children?: ReactNode }) => <td>{enrichChildren(children, byId, byLabel, onOpenShot)}</td>,
      th: ({ children }: { children?: ReactNode }) => <th>{enrichChildren(children, byId, byLabel, onOpenShot)}</th>,
      strong: ({ children }: { children?: ReactNode }) => (
        <strong>{enrichChildren(children, byId, byLabel, onOpenShot)}</strong>
      ),
      em: ({ children }: { children?: ReactNode }) => <em>{enrichChildren(children, byId, byLabel, onOpenShot)}</em>,
    }),
    [byId, byLabel, onOpenShot],
  )

  return (
    <div
      className={cn(
        "prose max-w-none text-xs leading-relaxed text-ui-text-primary dark:prose-invert",
        "prose-headings:my-2 prose-headings:text-xs prose-headings:font-semibold",
        "prose-p:my-1.5 prose-p:text-xs prose-ol:my-1.5 prose-ul:my-1.5 prose-li:my-0.5 prose-li:text-xs",
        "prose-pre:my-2 prose-pre:rounded-lg prose-pre:bg-ui-surface-sunken prose-pre:p-2 prose-pre:text-xs",
        "prose-code:rounded prose-code:bg-ui-surface-sunken prose-code:px-1 prose-code:py-0.5 prose-code:text-xs prose-code:before:content-none prose-code:after:content-none",
        "prose-a:text-ui-accent prose-th:text-xs prose-td:text-xs",
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
